import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChat } from '../hooks';
import { useChatChannel } from '../hooks/useChatChannel';
import { ChannelList } from '../components/ChannelList';
import { ChannelHeader } from '../components/ChannelHeader';
import { MessageList } from '../components/MessageList';
import { MessageInput } from '../components/MessageInput';
import { TypingIndicator } from '../components/TypingIndicator';
import { NewChatDialog } from '../components/NewChatDialog';
import { useAuth } from '@/auth';
import { FavoriteButton } from '../../../components/FavoriteButton';

export function ChatPage() {
  const { channelId: urlChannelId } = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeChannelId, setActiveChannelId] = useState<string | null>(urlChannelId || null);

  // Sync activeChannelId when the URL param changes (e.g. browser back/forward)
  useEffect(() => {
    setActiveChannelId(urlChannelId || null);
  }, [urlChannelId]);

  const { channels, messages, typing } = useChat(activeChannelId);
  const { channel: activeChannel, members, memberCount } = useChatChannel(activeChannelId);

  // Stable refs to avoid re-creating callbacks when messages/channels objects change
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  const handleSelectChannel = useCallback(
    (channelId: string) => {
      setActiveChannelId(channelId);
      navigate(`/a/arche_chat/channel/${channelId}`, { replace: true });
    },
    [navigate],
  );

  const handleCreateDM = useCallback(
    async (targetUserId: string) => {
      const channel = await channelsRef.current.openDirectMessage(targetUserId);
      if (channel) {
        handleSelectChannel(channel.channelId);
      }
    },
    [handleSelectChannel],
  );

  const handleCreateGroup = useCallback(
    async (name: string, description: string) => {
      const channel = await channelsRef.current.createChannel({
        channelType: 'group',
        name,
        description: description || undefined,
      });
      if (channel) {
        handleSelectChannel(channel.channelId);
      }
    },
    [handleSelectChannel],
  );

  const handleSendMessage = useCallback((content: string) => {
    messagesRef.current.sendMessage(content);
  }, []);

  const handleEditMessage = useCallback((messageId: string, content: string) => {
    messagesRef.current.editMessage(messageId, content);
  }, []);

  const handleDeleteMessage = useCallback((messageId: string) => {
    messagesRef.current.deleteMessage(messageId);
  }, []);

  // Get current user ID
  const currentUserId = user?.userId || null;

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Channel Sidebar */}
      <div className="w-64 border-r border-border bg-card flex flex-col flex-shrink-0">
        <div className="h-14 border-b border-border px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-foreground">Chat</h1>
            <FavoriteButton archeId="arche_chat" />
          </div>
          <NewChatDialog onCreateDM={handleCreateDM} onCreateGroup={handleCreateGroup} />
        </div>
        <ChannelList
          channels={channels.channels}
          activeChannelId={activeChannelId}
          onSelectChannel={handleSelectChannel}
          loading={channels.loading}
        />
      </div>

      {/* Message Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <ChannelHeader
          channel={activeChannel}
          memberCount={memberCount}
          currentUserId={currentUserId}
          members={members}
        />
        <MessageList
          messages={messages.messages}
          currentUserId={currentUserId}
          loading={messages.loading}
          loadingMore={messages.loadingMore}
          hasMore={messages.hasMore}
          onLoadMore={messages.loadMore}
          onEditMessage={handleEditMessage}
          onDeleteMessage={handleDeleteMessage}
        />
        <TypingIndicator
          typingUsers={typing.typingUsers.filter((u) => u.userId !== currentUserId)}
        />
        {activeChannelId && (
          <MessageInput
            onSend={handleSendMessage}
            onTyping={typing.sendTyping}
            disabled={!activeChannelId}
            placeholder={
              activeChannel
                ? `Message ${activeChannel.dmPartnerDisplayName || activeChannel.name || 'channel'}...`
                : 'Select a channel...'
            }
          />
        )}
      </div>
    </div>
  );
}
